import React, { useEffect, useMemo, useState } from "react";
import ClientLayout from "../../components/layouts/ClientLayout";
import { auth, database, firestore } from "../../firebaseConfig";
import { ref, onValue, get, set, remove } from "firebase/database";
import { doc, getDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import { ShoppingCart, Package, Clock, CheckCircle, XCircle, Plus, Milk, X, Calendar, MapPin, Trash2, Eye } from "lucide-react";
import { getClientDisplayName } from "../../utils/clientDisplayName";

const wrapUnsubscribe = (unsubscribe, companyName) => {
  if (typeof unsubscribe === "function") return unsubscribe;
  console.warn(`Listener for ${companyName} did not return an unsubscribe function.`);
  return () => {};
};

const getOrderTime = (order) => {
  const parsed = order?.orderDate ? Date.parse(order.orderDate) : NaN;
  return Number.isFinite(parsed) ? parsed : 0;
};

const createMergeScheduler = (ordersByCompany, setOrdersFn) => {
  let mergePromise = null;
  return () => {
    if (mergePromise) return;
    mergePromise = Promise.resolve().then(() => {
      mergePromise = null;
      const mergedOrders = Object.values(ordersByCompany).flat();
      mergedOrders.sort((a, b) => getOrderTime(b) - getOrderTime(a));
      setOrdersFn(mergedOrders);
    });
  };
};

/**
 * ClientOrders Component
 * 
 * Displays all orders placed by the client across all companies they're connected to.
 * Orders are fetched from Firebase Realtime Database at:
 * Companies/{companyName}/ClientOrders/{clientUsername}/orders/{orderId}
 */

const ClientOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [connectedCompanies, setConnectedCompanies] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [milkDeliveries, setMilkDeliveries] = useState({});
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { order, companyName }
  const [deleting, setDeleting] = useState(false);

  const [user, setUser] = useState(null);
  const clientId = useMemo(
    () => clientData?.otherName || user?.uid || null,
    [clientData, user]
  );

  // Set up auth listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Load client profile and connected companies
  useEffect(() => {
    if (!user) return;

    const loadClientData = async () => {
      try {
        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (!userDoc.exists()) return;

        const data = userDoc.data();
        setClientData(data);

        // Get list of companies the client is connected to
        // Stored in user document as array: connectedCompanies: ["CompanyA", "CompanyB"]
        const companies = data.connectedCompanies || [];
        setConnectedCompanies(companies);
      } catch (error) {
        console.error("Error loading client data:", error);
        toast.error("Failed to load profile");
      }
    };

    loadClientData();
  }, [user]);

  // Load orders from all connected companies
  useEffect(() => {
    if (!clientId) {
      setOrders([]);
      return;
    }

    if (connectedCompanies.length === 0 || !database) {
      setOrders([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const ordersByCompany = {};
    const seenInitial = new Set();
    const mergeAndSet = createMergeScheduler(ordersByCompany, setOrders);
    const unsubscribers = connectedCompanies.map((companyName) => {
      const ordersRef = ref(
        database,
        `Companies/${companyName}/ClientOrders/${clientId}/orders`
      );
      ordersByCompany[companyName] = [];

      const markInitial = () => {
        if (!seenInitial.has(companyName)) {
          seenInitial.add(companyName);
          if (seenInitial.size === connectedCompanies.length) {
            setLoading(false);
          }
        }
      };

      const unsubscribe = onValue(
        ordersRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const ordersData = snapshot.val();
            ordersByCompany[companyName] = Object.entries(ordersData).map(
              ([id, order]) => ({
                id,
                ...order,
                companyName,
              })
            );
          } else {
            ordersByCompany[companyName] = [];
          }
          markInitial();
          mergeAndSet();
        },
        (error) => {
          console.error(`Error loading orders from ${companyName}:`, error);
          ordersByCompany[companyName] = [];
          markInitial();
          mergeAndSet();
        }
      );

      return wrapUnsubscribe(unsubscribe, companyName);
    });

    // Cleanup
    return () => {
      unsubscribers.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [clientId, connectedCompanies, database]);

  // Load milk delivery data for each company
  useEffect(() => {
    if (!clientId || connectedCompanies.length === 0 || !database) return;

    const loadMilkDeliveries = async () => {
      const deliveries = await Promise.all(
        connectedCompanies.map(async (companyName) => {
          try {
            const purchasesRef = ref(
              database,
              `Companies/${companyName}/ClientPurchases/${clientId}/purchases`
            );
            const purchasesSnap = await get(purchasesRef);

            if (purchasesSnap.exists()) {
              const data = purchasesSnap.val();
              let totalLiters = 0;
              let totalAmount = 0;
              let deliveryCount = 0;

              Object.values(data).forEach((yearData) => {
                Object.values(yearData || {}).forEach((monthData) => {
                  Object.values(monthData || {}).forEach((purchase) => {
                    totalLiters += Number(purchase.liters || 0);
                    totalAmount += Number(purchase.totalAmount || 0);
                    deliveryCount += 1;
                  });
                });
              });

              return [
                companyName,
                {
                  totalLiters,
                  totalAmount,
                  deliveryCount,
                },
              ];
            }
          } catch (error) {
            console.error(`Error loading deliveries from ${companyName}:`, error);
          }
          return null;
        })
      );

      // Ensure we only convert well-formed [companyName, stats] tuples into the map.
      const validEntries = deliveries.filter(
        (entry) => Array.isArray(entry) && entry.length === 2
      );
      setMilkDeliveries(Object.fromEntries(validEntries));
    };

    loadMilkDeliveries();
  }, [clientId, connectedCompanies, database]);

  const { filteredOrders, stats } = useMemo(() => {
    const totals = {
      total: 0,
      pending: 0,
      completed: 0,
      cancelled: 0,
    };
    const filtered = [];
    const validStatuses = new Set(["pending", "completed", "cancelled"]);

    orders.forEach((order) => {
      const status = order.status?.toLowerCase() || "";
      totals.total += 1;
      if (validStatuses.has(status)) {
        totals[status] += 1;
      }

      if (selectedStatus === "all" || status === selectedStatus) {
        filtered.push(order);
      }
    });

    return { filteredOrders: filtered, stats: totals };
  }, [orders, selectedStatus]);

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "seen":
        return <Eye className="w-4 h-4 text-blue-600" />;
      case "rejected":
        return <XCircle className="w-4 h-4 text-red-600" />;
      case "pending":
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "cancelled":
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Package className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "seen":
        return "bg-blue-100 text-blue-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Delete order handler
  const handleDeleteOrder = async () => {
    if (!deleteConfirm || !clientId) return;

    setDeleting(true);
    try {
      const orderRef = ref(
        database,
        `Companies/${deleteConfirm.companyName}/ClientOrders/${clientId}/orders/${deleteConfirm.order.id}`
      );
      
      await remove(orderRef);
      toast.success("Order deleted successfully");
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Failed to delete order. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading orders...</div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-yellow-700 flex items-center gap-2">
              <ShoppingCart className="w-8 h-8" />
              My Orders
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              View and track all your orders
            </p>
          </div>
          <button
            onClick={() => setShowOrderModal(true)}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Order
          </button>
        </div>

        {/* Milk Delivery Summary by Company */}
        {connectedCompanies.length > 0 && Object.keys(milkDeliveries).length > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 shadow">
            <h2 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2">
              <Milk className="w-6 h-6" />
              Milk Deliveries by Company
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {connectedCompanies.map((companyName) => {
                const delivery = milkDeliveries[companyName];
                if (!delivery) return null;
                return (
                  <div
                    key={companyName}
                    className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition"
                  >
                    <h3 className="font-semibold text-gray-800 mb-3">{companyName}</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Total Liters:</span>
                        <span className="font-bold text-blue-700">{delivery.totalLiters.toFixed(2)}L</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Total Amount:</span>
                        <span className="font-bold text-green-700">KES {delivery.totalAmount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Deliveries:</span>
                        <span className="font-bold text-gray-700">{delivery.deliveryCount}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Orders" value={stats.total} color="bg-blue-100 text-blue-700" />
          <StatCard label="Pending" value={stats.pending} color="bg-yellow-100 text-yellow-700" />
          <StatCard label="Completed" value={stats.completed} color="bg-green-100 text-green-700" />
          <StatCard label="Cancelled" value={stats.cancelled} color="bg-red-100 text-red-700" />
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 border-b pb-2">
          <FilterButton
            label="All"
            active={selectedStatus === "all"}
            onClick={() => setSelectedStatus("all")}
          />
          <FilterButton
            label="Pending"
            active={selectedStatus === "pending"}
            onClick={() => setSelectedStatus("pending")}
          />
          <FilterButton
            label="Completed"
            active={selectedStatus === "completed"}
            onClick={() => setSelectedStatus("completed")}
          />
          <FilterButton
            label="Cancelled"
            active={selectedStatus === "cancelled"}
            onClick={() => setSelectedStatus("cancelled")}
          />
        </div>

        {/* No connected companies */}
        {connectedCompanies.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <ShoppingCart className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
              No Companies Connected
            </h3>
            <p className="text-yellow-700 mb-4">
              Connect to companies to start placing orders
            </p>
            <a
              href="/client/settings"
              className="inline-block px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              Go to Settings
            </a>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              No Orders Found
            </h3>
            <p className="text-gray-500">
              {selectedStatus === "all"
                ? "You haven't placed any orders yet"
                : `No ${selectedStatus} orders found`}
            </p>
          </div>
        ) : (
          /* Orders List */
          <div className="space-y-3">
            {filteredOrders.map((order) => (
              <OrderCard 
                key={order.id} 
                order={order} 
                getStatusIcon={getStatusIcon} 
                getStatusColor={getStatusColor}
                onDelete={() => setDeleteConfirm({ order, companyName: order.companyName })}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => !deleting && setDeleteConfirm(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Delete Order</h3>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 mb-2">
                <strong>Order ID:</strong> {deleteConfirm.order.orderId || deleteConfirm.order.id}
              </p>
              <p className="text-sm text-red-800 mb-2">
                <strong>Company:</strong> {deleteConfirm.companyName}
              </p>
              <p className="text-sm text-red-800">
                <strong>Amount:</strong> KES {Number(deleteConfirm.order.totalAmount || 0).toLocaleString()}
              </p>
            </div>

            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this order? This will permanently remove the order from your records.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteOrder}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Order
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Order Modal */}
      {showOrderModal && (
        <NewOrderModal
          open={showOrderModal}
          onClose={() => setShowOrderModal(false)}
          connectedCompanies={connectedCompanies}
          clientData={clientData}
          clientId={clientId}
        />
      )}
    </ClientLayout>
  );
};

// Helper Components
const StatCard = ({ label, value, color }) => (
  <div className={`rounded-xl p-4 shadow ${color}`}>
    <p className="text-sm font-medium">{label}</p>
    <p className="text-2xl font-bold mt-1">{value}</p>
  </div>
);

const FilterButton = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg font-medium transition ${
      active
        ? "bg-yellow-600 text-white"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
    }`}
  >
    {label}
  </button>
);

const OrderCard = ({ order, getStatusIcon, getStatusColor, onDelete }) => (
  <div className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
    <div className="flex justify-between items-start mb-3">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-800">Order #{order.orderId || order.id}</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(order.status)}`}>
            {getStatusIcon(order.status)}
            {order.status || "Unknown"}
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Company: <span className="font-medium">{order.companyName}</span>
        </p>
      </div>
      <div className="flex items-start gap-3">
        <div className="text-right">
          <p className="text-lg font-bold text-yellow-700">
            KES {Number(order.totalAmount || 0).toLocaleString()}
          </p>
          <p className="text-xs text-gray-400">
            {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : "—"}
          </p>
        </div>
        <button
          onClick={onDelete}
          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
          title="Delete order"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>

    {/* Order Items */}
    {order.items && order.items.length > 0 && (
      <div className="border-t pt-3 space-y-1">
        <p className="text-sm font-medium text-gray-700 mb-2">Items:</p>
        {order.items.map((item, idx) => (
          <div key={idx} className="flex justify-between text-sm">
            <span className="text-gray-600">
              {item.name} x {item.quantity}
            </span>
            <span className="text-gray-800 font-medium">
              KES {Number(item.price || 0).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    )}

    {order.companyResponseNote && (
      <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
        <p className="text-xs font-semibold text-blue-800">Company response</p>
        <p className="text-sm text-blue-700 whitespace-pre-line mt-1">
          {order.companyResponseNote}
        </p>
      </div>
    )}

    {/* Delivery Info */}
    {order.deliveryAddress && (
      <div className="mt-3 pt-3 border-t">
        <p className="text-xs text-gray-500">
          Delivery: {order.deliveryAddress}
        </p>
      </div>
    )}
  </div>
);

// New Order Modal Component
const NewOrderModal = ({ open, onClose, connectedCompanies, clientData, clientId }) => {
  const [selectedCompany, setSelectedCompany] = useState("");
  const [quantity, setQuantity] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Set default delivery date to tomorrow
  useEffect(() => {
    if (!deliveryDate) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setDeliveryDate(tomorrow.toISOString().split('T')[0]);
    }
  }, [deliveryDate]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCompany) {
      toast.error("Please select a company");
      return;
    }

    if (!clientId) {
      toast.error("Missing client profile. Please re-login.");
      return;
    }

    if (!quantity || parseFloat(quantity) <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }

    if (!deliveryDate) {
      toast.error("Please select a delivery date");
      return;
    }

    if (!deliveryAddress.trim()) {
      toast.error("Please enter a delivery address");
      return;
    }

    setSubmitting(true);

    try {
      // Get unit price from company's validated clients
      const clientRef = ref(
        database,
        `Companies/${selectedCompany}/ValidatedClients/${clientId}`
      );
      const clientSnap = await get(clientRef);
      
      let unitPrice = 0;
      if (clientSnap.exists()) {
        unitPrice = clientSnap.val().UnitPrice || 0;
      }

      const totalAmount = parseFloat(quantity) * unitPrice;

      const clientDisplayName = getClientDisplayName(clientData);

      // Create order object
      const orderData = {
        orderId: `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        clientId,
        clientName: clientDisplayName || "Unknown",
        clientEmail: clientData.email || "",
        clientPhone: clientData.phoneNumber || "",
        quantity: parseFloat(quantity),
        unitPrice: unitPrice,
        totalAmount: totalAmount,
        deliveryDate: deliveryDate,
        deliveryAddress: deliveryAddress,
        notes: notes || "",
        status: "pending",
        orderDate: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };

      // Save to Firebase RTDB
      const orderRef = ref(
        database,
        `Companies/${selectedCompany}/ClientOrders/${clientId}/orders/${orderData.orderId}`
      );

      await set(orderRef, orderData);

      toast.success("Order placed successfully!");
      onClose();
      
      // Reset form
      setSelectedCompany("");
      setQuantity("");
      setDeliveryAddress("");
      setNotes("");
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Failed to place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Milk className="w-6 h-6 text-yellow-600" />
            Place New Milk Order
          </h2>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Company Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Company <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              disabled={submitting}
              required
            >
              <option value="">-- Select a company --</option>
              {connectedCompanies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity (Liters) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="e.g., 50"
              min="0.1"
              step="0.1"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              disabled={submitting}
              required
            />
          </div>

          {/* Delivery Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Delivery Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              disabled={submitting}
              required
            />
          </div>

          {/* Delivery Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Delivery Address <span className="text-red-500">*</span>
            </label>
            <textarea
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              placeholder="Enter full delivery address with landmarks"
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              disabled={submitting}
              required
            />
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special instructions or requirements"
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              disabled={submitting}
            />
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> Your order will be sent to the selected company for processing. 
              You will be notified once the order is confirmed. Price will be calculated based on 
              the company's current rate.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4" />
                  Place Order
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientOrders;
